/**
 * DialerCallPane — right-side pane inside My Chats that becomes the DIALER
 * COCKPIT while a progressive-dial queue is running for this agent.
 *
 * It replaces LeadProfilePanel whenever a dialer queue is active. Two modes,
 * driven by the queue's callState (from GET /api/ai/voice/dialer/status):
 *
 *   callState 'dialing'  → the call is live. Show a big "End Call" button + a
 *                          live talk timer. End Call hangs up the line (the buyer
 *                          may already have hung up — the agent can still end).
 *   callState 'ended'    → the call is over. Show the DISPOSITION FORM — the
 *                          fixed outcome taxonomy (Order Placed / Interested -
 *                          Followup — Date Confirmed / Call back - Details
 *                          Shared / Cx Want on COD / Not Interested / Not
 *                          Contacted), same as
 *                          the standalone click-to-call popup and the Personal
 *                          Call popup. Submit → log the outcome AND dial the
 *                          next number.
 *
 * On Submit we post BOTH:
 *   1. /api/ai/voice/call-outcome (REACT_APP_AI_PATH) — writes the CallReport,
 *      stamps the outcome on the WhatsApp conversation, and for "Not Interested"
 *      routes through the CANONICAL markBuyerNotInterested() flow (75% monthly
 *      cap + Personal-Use routing). HOT/WARM also auto-"keep" the lead for this
 *      agent so it can't flip to someone else before the follow-up.
 *   2. /api/ai/voice/dialer/disposition (REACT_APP_AI_PATH) — advances the queue
 *      (or pauses it when the outcome is "Order Placed").
 *
 * The parent (MyChats) owns the status polling and switches the middle chat pane
 * to the dialed number; this pane receives that status via the `dialer` prop and
 * calls back through onAdvance/onStop so the parent stays in sync.
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { preopenFollowUpWa } from '../../utils/whatsappFollowUp';
import {
  Box, Typography, TextField, Button, Select, MenuItem, FormControl,
  InputLabel, CircularProgress, IconButton, Collapse,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';
import PauseCircleOutlineIcon from '@material-ui/icons/PauseCircleOutline';
import CallEndIcon from '@material-ui/icons/CallEnd';
import RefreshIcon from '@material-ui/icons/Refresh';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WhatshotIcon from '@material-ui/icons/Whatshot';
import InfoIcon from '@material-ui/icons/Info';
import AcUnitIcon from '@material-ui/icons/AcUnit';
import BlockIcon from '@material-ui/icons/Block';
import EventIcon from '@material-ui/icons/Event';
import SendIcon from '@material-ui/icons/Send';
import CohortCallDisposition from '../../ui-component/CohortCallDisposition';
import { PrimeDialerBanner } from '../../ui-component/PrimeBadge';
import CallMadeIcon from '@material-ui/icons/CallMade';
import CallReceivedIcon from '@material-ui/icons/CallReceived';
import CallMissedIcon from '@material-ui/icons/CallMissed';
import PhoneMissedIcon from '@material-ui/icons/PhoneMissed';
import LeadScoreBar, { LEAD_SCORE_AUTO, scoreNeeded, scoreContradiction } from '../../ui-component/LeadScoreBar';

// The 5 fixed outcomes (Shailendra 2026-08-27, dates updated 2026-08-29) — same
// taxonomy as the standalone click-to-call popup (ui-component/SoftphoneCall.js)
// and the Personal Call popup (Leads/UnregisteredLeadsPopup.js). Only HOT and
// WARM require a future follow-up date/time — COLD no longer asks for one.
// Posts to POST /api/ai/voice/call-outcome, THEN /api/ai/voice/dialer/disposition
// to actually advance/pause the queue.
const OUTCOMES = [
  { value: 'WON',  label: 'Order Placed',                 sub: 'Logged as a completed sale — dialer pauses so you can punch the order.', tag: 'WON',  dateMode: 'none',     legacyStatus: 'Order placed', Icon: CheckCircleIcon, color: '#1b5e20', bg: '#e8f5e9' },
  { value: 'HOT',  label: 'Interested - Followup — Date Confirmed', sub: 'Buyer will be auto-dialled again at the time you set.',                   tag: 'HOT',  dateMode: 'required', legacyStatus: 'Follow-up',    Icon: WhatshotIcon,    color: '#c62828', bg: '#fdecea' },
  { value: 'WARM', label: 'Call back - Details Shared',    sub: 'They asked for more info — schedule a callback.',                         tag: 'WARM', dateMode: 'required', legacyStatus: 'Follow-up',    Icon: InfoIcon,        color: '#e65100', bg: '#fff4e5' },
  { value: 'COLD', label: 'Cx Want on COD - Advance Payment Issue', sub: 'Wants COD, not ready to pay advance — no follow-up scheduled.',                                 tag: 'COLD', dateMode: 'none',     legacyStatus: 'Follow-up',    Icon: AcUnitIcon,      color: '#1565c0', bg: '#e8f2fd' },
  { value: 'DEAD', label: 'Not Interested',                sub: 'No follow-up will be scheduled for this buyer.',                          tag: 'DEAD', dateMode: 'none',     legacyStatus: 'Not Interested', Icon: BlockIcon,     color: '#616161', bg: '#f5f5f5' },
  { value: 'SCOPE', label: 'Not Contacted',                sub: "Call didn't lead to a real conversation.",                    tag: '', dateMode: 'none', legacyStatus: 'Not Contacted', Icon: PhoneMissedIcon, color: '#78909c', bg: '#eceff1' },
];

// Client-side safety net for the shared productCategories dropdown — always
// guarantee these are offered even if they aren't in that StaticData doc yet.
const ENSURE_CATEGORIES = ['Diaper', 'No Gas Deo'];
// Fallback Product Category when nothing on the conversation tells us one —
// the agent can still change it since the field is unlocked in that case.
const DEFAULT_CATEGORY = 'No Gas Deo';

// ── PENDING DISPOSITION LATCH (Shailendra 2026-10-21) ─────────────────────
// Agents were escaping the wrap-up form by REFRESHING the browser: React state
// dies with the page, the pane comes back idle, and the call is never
// dispositioned. Measured 2026-10-21: 194 of 499 connected calls (39%) ended
// with no disposition — Madhuri 25, Saloni 20, Sonu 19.
//
// Any client-side gate (disabling SCOPE, hiding the skip button) dies with the
// same refresh, so the latch has to OUTLIVE the page. localStorage does.
//
// Set the moment the form opens on a CONNECTED call; cleared only when the
// disposition actually posts. While it is set the pane re-opens the form after
// a reload instead of letting the agent walk away from it.
//
// ⚠️ NOT a security boundary — localStorage is per-browser and clearable. It
// removes the easy escape the floor is using today; the durable guard is the
// server refusing to dial next while a connected call is undispositioned.
const PENDING_DISP_KEY = 'pendingDisposition';
const setPendingDisposition = (phone, talktime) => {
  try {
    localStorage.setItem(PENDING_DISP_KEY, JSON.stringify({ phone, talktime, at: Date.now() }));
  } catch (e) { /* private mode / quota — never block the dialler over it */ }
};
const clearPendingDisposition = () => {
  try { localStorage.removeItem(PENDING_DISP_KEY); } catch (e) { /* ignore */ }
};
const readPendingDisposition = () => {
  try {
    const v = JSON.parse(localStorage.getItem(PENDING_DISP_KEY) || 'null');
    // Stale latch guard: a flag older than one shift is a leftover, not a real
    // pending form — never strand an agent behind yesterday's call.
    if (v && v.at && (Date.now() - v.at) < 12 * 3600 * 1000) return v;
    if (v) clearPendingDisposition();
  } catch (e) { /* ignore */ }
  return null;
};

// Same reason list as the WhatsApp lead's own "Buyer Not Interested" 3-dot
// menu — DEAD here goes through the exact same canonical backend flow.
// Trimmed to three (Shailendra 2026-10-16) — Enquiry Only, Unreachable and
// Junk Lead dropped; the OTHERS outcome was removed from the form the same day.
const NI_REASONS = [
  'Not Interested — Rates Issue',
  'Not Interested — By Mistake Request',
  'Not Interested - Personal Use Only',
];

// Not Contacted is a single option now — no reason dropdown (Shailendra
// 2026-10-16). The backend still wants a reason, so send a fixed one that
// matches the old "Not Contacted - …" rows the reports already group on.
const SCOPE_REASON = 'Not Contacted - Ringing & Network Error';

// Reason dropdowns open downward from their own input box (MUI v4 otherwise
// overlays the menu on the selected item, which can land at the form bottom).
const DROPDOWN_BELOW = {
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  getContentAnchorEl: null,
};

const AI_PATH = (process.env.REACT_APP_AI_PATH || '').trim();
const API_PATH = (process.env.REACT_APP_PATH || '').trim();

const SIDEBAR_W = 300;
const C = {
  primary: '#1565c0',
  danger:  '#c62828',
  text:    '#1f2937',
  muted:   '#6b7280',
  border:  '#e0e0e0',
  bgSoft:  '#eef4fd',
  good:    '#1b5e20',
};

// Lead-source badge. FRESH = a brand-new lead (received in the last 48h, never
// messaged by an agent, never connected on a call) — the agent should give it
// extra attention, so we label it wherever the number is shown. MISSED = we rang
// this buyer before and never got through. Anything else stays unlabelled to keep
// the pane quiet.
const BUCKET_BADGE = {
  fresh:                 { label: 'FRESH',     bg: '#e8f5e9', fg: '#1b5e20', bd: '#a5d6a7' },
  fresh_redistributed:   { label: 'FRESH',     bg: '#e8f5e9', fg: '#1b5e20', bd: '#a5d6a7' },
  dialled_not_connected: { label: 'MISSED',    bg: '#fff4e5', fg: '#a15c00', bd: '#ffcc80' },
  followup:              { label: 'FOLLOW-UP', bg: '#e8f2fd', fg: '#1565c0', bd: '#90caf9' },
};

const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : null;

export function LeadBadge({ bucket, size = 'md' }) {
  const b = BUCKET_BADGE[bucket];
  if (!b) return null;
  const pad = size === 'sm' ? '1px 6px' : '3px 10px';
  const fs = size === 'sm' ? 9 : 11;
  return (
    <span style={{
      display: 'inline-block', padding: pad, borderRadius: 999,
      background: b.bg, color: b.fg, border: `1px solid ${b.bd}`,
      fontSize: fs, fontWeight: 800, letterSpacing: 0.5, lineHeight: 1.4,
    }}>
      {b.label}
    </span>
  );
}

function fmtDur(sec) {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

// direction + talktime -> icon/color/label for the "number history" rows
function numberHistoryVisual(c) {
  const connected = (c.talktime || 0) > 0;
  if (c.direction === 'inbound') {
    return connected
      ? { Icon: CallReceivedIcon, color: '#2e7d32', label: 'Connected' }
      : { Icon: CallMissedIcon, color: '#c62828', label: 'Missed' };
  }
  return connected
    ? { Icon: CallMadeIcon, color: '#1565c0', label: 'Connected' }
    : { Icon: CallMadeIcon, color: '#9e9e9e', label: 'No answer' };
}

const COOLDOWN_SECONDS = 3;

// `cohort` swaps the wrap-up form for the REPEAT team's own one
// (CohortCallDisposition), exactly as SoftphoneButton already does for manual
// click-to-call. Opt-in: new-revenue mounts (MyChats) pass nothing and keep the
// WON/HOT/WARM/COLD/DEAD form untouched.
//
// ⚠️ WHY THIS EXISTS. The cohort/new-rev split was by COMPONENT, not by team —
// DialerCallPane hardcoded the new-revenue 7 and never checked cohortTeam. So
// cohort agents on the dialler were shown "Wants rate / sample / details" for
// buyers who ALREADY buy from us, with no Old Stock option at all, and their
// answers went to /voice/call-outcome instead of /voice/cohort-outcome
// (Shailendra 2026-09-22: "ensure that all cohort agents whether calling from
// predective/progressive or manual are using the same form and calling the same
// api").
export default function DialerCallPane({ dialer, waSummarySent = false, waSummaryError = '', waChatOpen = true, onEnded, onSubmitted, onResume, onStop, onDialNext, onHold, onRefreshChat, onOpenChat, showBuyerInfo = true, isPredictive, cohort = false }) {
  const current = dialer?.current || null;
  const callState = dialer?.callState || null;     // 'dialing' | 'ended' | null
  const state = dialer?.state || null;             // running | awaiting_disposition | cooldown | paused | done
  const phone = current?.phone || '';
  const inCooldown = state === 'cooldown';

  // Mandatory pre-disposition WhatsApp call-summary step (Shailendra 2026-09-07):
  // every CONNECTED outgoing call (callState 'ended' already implies talktime>0)
  // whose conversation has an OPEN Meta 24h window must get a short free-text
  // WA message before the outcome form appears. whatsappWindowOpen is computed
  // server-side in voizDialer.status() (same isInsideReplyWindow formula
  // whatsappInbox.js uses) — if the window is closed, this step is skipped
  // entirely and behaviour is unchanged.
  //
  // 2026-09-30 (Shailendra): NO separate input box here any more — agents read
  // it as the disposition remark box ("cx busy", "call back later") and those
  // notes went to the buyer on WhatsApp. The message is now typed in the
  // normal My Chats composer + Send, so it's obviously a buyer message.
  // MyChats owns the send and tells us via waSummarySent / waSummaryError.
  const whatsappWindowOpen = !!current?.whatsappWindowOpen;
  const [waSummaryBypassed, setWaSummaryBypassed] = useState(false);
  // Gate the outcome form on this step ONLY while the window is open and
  // nothing has resolved it yet (sent, or fail-open bypass after a send error).
  const needsWaSummary = whatsappWindowOpen && !waSummarySent && !waSummaryBypassed;
  // Survives a page refresh: set when a CONNECTED call's form opens, cleared
  // only when the disposition posts. Re-checked whenever the pane re-renders
  // so a reload lands back on the form instead of an idle pane.
  const [pendingDisp, setPendingDisp] = useState(() => readPendingDisposition());
  useEffect(() => {
    if (callState === 'ended') setPendingDisp(readPendingDisposition());
  }, [callState]);

  const [form, setForm] = useState({ outcome: '', followUpDateTime: '', callRemark: '', niReason: '', extraReason: '', sellerName: '', productCategory: '', leadScore: null });
  const [categoryOptions, setCategoryOptions] = useState([]);
  // Locked = we already know it (Seller/Leads/conversation, or the AI-detected
  // product) — read-only. Unlocked = we have no idea, so it's optional and the
  // agent can fill it in (Shailendra 2026-08-29).
  const [nameLocked, setNameLocked] = useState(false);
  const [categoryLocked, setCategoryLocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ending, setEnding] = useState(false);
  const [tick, setTick] = useState(0); // re-render the live timer
  const lastPhoneRef = useRef('');

  // This agent's own past attempts to the number they're currently dialing —
  // "have I already called this lead, did it connect, for how long" — shown
  // under the Wrong Number/skip button. Shailendra 2026-08-31.
  const [numberHistory, setNumberHistory] = useState([]);
  // Which history row's recording is currently expanded/playing (2026-09-15) —
  // one at a time, toggled by the play/pause icon next to each connected call.
  const [playingCallId, setPlayingCallId] = useState(null);
  const [numberHistoryLoading, setNumberHistoryLoading] = useState(false);

  // Buyer context for the ON CALL card — name, last order, last call, plus the
  // WhatsApp side (WA name, ad campaign they clicked, product, first message,
  // conversationId for Open Chat). Fetched fresh whenever the dialed number
  // changes. On for every pane — cohort + retail, predictive + progressive
  // (Shailendra 2026-09-29; was cohort-only before).
  const [buyerInfo, setBuyerInfo] = useState(null);
  useEffect(() => {
    if (!showBuyerInfo || !phone) { setBuyerInfo(null); return; }
    let live = true;
    setBuyerInfo(null);
    axios.get(AI_PATH + '/api/ai/voice/dialer/buyer-info', { params: { phone } })
      .then((r) => { if (live && r.data?.success) setBuyerInfo(r.data); })
      .catch(() => {});
    return () => { live = false; };
  }, [showBuyerInfo, phone]);

  // Open Chat — manual fallback for when the chat pane didn't follow the call.
  // Inside My Chats the parent switches its middle pane (onOpenChat); anywhere
  // else (cohort floating cockpit) open the chat deep link in a new tab.
  const waConvoId = buyerInfo?.whatsapp?.conversationId || null;
  const openChat = () => {
    if (!waConvoId) return;
    if (onOpenChat) { onOpenChat(waConvoId, phone); return; }
    window.open(`/crm/dashboard/my-chats?chat=${waConvoId}`, '_blank', 'noopener');
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  // when the dialed number changes, reset the outcome form + auto-fill
  // Name (Seller → Leads → WhatsApp conversation) and Product Category
  // (whatever the conversation already knows) — both stay editable.
  useEffect(() => {
    const p10 = String(phone).replace(/\D/g, '').slice(-10);
    if (p10 && p10 !== lastPhoneRef.current) {
      lastPhoneRef.current = p10;
      // ── Tell the WhatsApp-check extension which buyer to watch ────────────
      // The extension cannot read the buyer's number off WhatsApp Web: verified
      // 2026-09-15 that data-id is a bare message id, the header shows only an
      // avatar letter, and every message-in/out class is gone. So the CRM hands
      // it over instead (Shailendra 2026-09-15: "as soon as the deposition form
      // opens it can save the number in the localstorage and the plugin can
      // read from there"). The extension polls this, watches for an outgoing
      // message, and posts the confirmation that unlocks Submit.
      try {
        localStorage.setItem('waCheckExpected', JSON.stringify({
          phone: p10,
          name: '',                    // filled below once call-context resolves
          at: Date.now(),
        }));
      } catch (e) { /* private mode / quota — never block the dialler over it */ }
      // Latch the pending disposition so a browser refresh cannot drop it.
      if (Number(current?.talktime) > 0) setPendingDisposition(p10, Number(current.talktime));
      setForm({ outcome: '', followUpDateTime: '', callRemark: '', niReason: '', extraReason: '', sellerName: '', productCategory: DEFAULT_CATEGORY, leadScore: null });
      setCategoryOptions([]);
      setNameLocked(false); setCategoryLocked(false);
      setNumberHistory([]);
      setWaSummaryBypassed(false);
      setNumberHistoryLoading(true);
      axios.get(AI_PATH + '/api/ai/voice/number-history', { params: { phone: p10 } })
        .then((r) => { if (lastPhoneRef.current === p10) setNumberHistory(r.data?.calls || []); })
        .catch(() => {})
        .finally(() => { if (lastPhoneRef.current === p10) setNumberHistoryLoading(false); });
      axios.get(AI_PATH + '/api/ai/voice/call-context', { params: { phone: p10 } })
        .then((r) => {
          if (lastPhoneRef.current !== p10) return;
          if (r.data?.name) {
            setForm((prev) => ({ ...prev, sellerName: r.data.name }));
            setNameLocked(true);
            // Hand the buyer's NAME to the extension too. WhatsApp no longer
            // marks outgoing rows, so the extension separates the agent's
            // messages from the buyer's by sender name — without this it falls
            // back to a weaker test.
            try {
              const cur = JSON.parse(localStorage.getItem('waCheckExpected') || '{}');
              if (cur.phone === p10) {
                localStorage.setItem('waCheckExpected', JSON.stringify({ ...cur, name: r.data.name }));
              }
            } catch (e) { /* ignore */ }
          }
          if (r.data?.productCategory) { setForm((prev) => ({ ...prev, productCategory: r.data.productCategory })); setCategoryLocked(true); }
        })
        .catch(() => {});
      axios.post(API_PATH + '/sales/callDropdowns?filterType=productCategories')
        .then((r) => {
          if (lastPhoneRef.current !== p10) return;
          const cats = r.data?.data || [];
          const merged = [...cats];
          ENSURE_CATEGORIES.forEach((c) => { if (!merged.includes(c)) merged.push(c); });
          setCategoryOptions(merged);
        })
        .catch(() => setCategoryOptions([...ENSURE_CATEGORIES]));
    }
  }, [phone]); // eslint-disable-line

  // live talk timer while dialing
  useEffect(() => {
    if (callState !== 'dialing') return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [callState]);

  // COOLDOWN 3-2-1 countdown after a no-answer / buyer-disconnect. Counts down,
  // then fires onDialNext() once. Runs a single time per cooldown entry (guarded
  // by a ref) so the countdown doesn't restart on every status poll.
  const [countdown, setCountdown] = useState(COOLDOWN_SECONDS);
  const cooldownFiredRef = useRef(false);
  useEffect(() => {
    if (!inCooldown) { cooldownFiredRef.current = false; setCountdown(COOLDOWN_SECONDS); return undefined; }
    if (cooldownFiredRef.current) return undefined;
    setCountdown(COOLDOWN_SECONDS);
    const t = setInterval(() => {
      setCountdown((n) => {
        if (n <= 1) {
          clearInterval(t);
          if (!cooldownFiredRef.current) { cooldownFiredRef.current = true; onDialNext && onDialNext(); }
          return 0;
        }
        return n - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [inCooldown]); // eslint-disable-line

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const elapsed = current?.dialedAt ? Math.floor((Date.now() - new Date(current.dialedAt).getTime()) / 1000) : 0;

  // End the current call. `connected` = did the agent actually talk to the buyer?
  //   true  → wrap-up form (real conversation)
  //   false → cooldown, no form (wrong/invalid number, no pickup) — then next dial
  // Both paths really hang up on VOIZ and only report the call ended once VOIZ
  // confirms the line is free. If VOIZ still shows it connected, the pane stays
  // on the live view with a warning — never "ended" here while the softphone is
  // still talking. The status poll flips it when the call actually ends.
  const [endWarning, setEndWarning] = useState('');
  useEffect(() => { if (callState !== 'dialing') setEndWarning(''); }, [callState]);
  const endCall = async (connected) => {
    setEnding(true);
    setEndWarning('');
    try {
      const r = isPredictive
        ? await axios.post(AI_PATH + '/api/ai/voice/predictive/end-call', {})
        : await axios.post(AI_PATH + '/api/ai/voice/dialer/end-current', { connected, talktime: elapsed });
      if (r.data?.lineStillLive) setEndWarning(r.data.message || 'VOIZ still shows this call connected — hang up on the softphone.');
      onEnded && onEnded();
    } catch (e) {
      setEndWarning(e?.response?.data?.message || 'Could not end the call — hang up on the softphone.');
      onEnded && onEnded();
    }
    finally { setEnding(false); }
  };

  // Redial the SAME number — call got cut by mistake / connected wrongly.
  // Predictive has no progressive queue, so /voice/dialer/redial always said
  // "No active dialer queue" — it has its own endpoint that dials the same
  // number from the agent's session (2026-10-15).
  const [redialing, setRedialing] = useState(false);
  const [redialError, setRedialError] = useState('');
  const redial = async () => {
    setRedialing(true);
    setRedialError('');
    try {
      const r = isPredictive
        ? await axios.post(AI_PATH + '/api/ai/voice/predictive/redial', { phone })
        : await axios.post(AI_PATH + '/api/ai/voice/dialer/redial', {});
      if (r.data && r.data.success === false) setRedialError(r.data.message || 'Redial failed.');
      onEnded && onEnded(); // re-poll → pane flips back to the live-call view
    } catch (e) {
      setRedialError(e?.response?.data?.message || 'Redial failed — try again.');
    }
    finally { setRedialing(false); }
  };

  const redialButton = (
    <Box>
      <Button
        fullWidth variant="text" size="small"
        disabled={redialing || submitting}
        onClick={redial}
        style={{ marginTop: 6, color: C.primary, fontWeight: 700, textTransform: 'none' }}
      >
        {redialing ? 'Redialing…' : '↻ Redial this number (cut by mistake)'}
      </Button>
      {redialError && (
        <Typography style={{ fontSize: 11.5, color: C.danger, fontWeight: 700, textAlign: 'center' }}>
          {redialError}
        </Typography>
      )}
    </Box>
  );

  const outcomeMeta = OUTCOMES.find((o) => o.value === form.outcome) || null;
  const isOrder = form.outcome === 'WON';
  const isDead = form.outcome === 'DEAD';
  const needsExtraReason = form.outcome === 'SCOPE';
  const needsDate = outcomeMeta?.dateMode === 'required';

  const submit = async () => {
    setSubmitting(true);
    // HOT / WARM → open WhatsApp to the buyer with the agent's intro pre-typed.
    // Opened here, inside the click, so the popup blocker allows it.
    const wa = preopenFollowUpWa(phone, form.outcome === 'HOT' || form.outcome === 'WARM');
    try {
      let followUpTime = null;
      if (needsDate && form.followUpDateTime) followUpTime = new Date(form.followUpDateTime).toISOString();

      // Predictive: ONE call does both halves progressive needs two calls for —
      // records the outcome (same recordCallOutcome() the line below uses) AND
      // clears Phonomenal's session so the engine can ring again. There's no
      // queue to advance, so /voice/dialer/disposition is never called here.
      if (isPredictive) {
        try {
          await axios.post(AI_PATH + '/api/ai/voice/predictive/session/dispose', {
            outcome: form.outcome,
            followUpAt: followUpTime,
            remark: (isDead || needsExtraReason) ? '' : form.callRemark,
            notInterestedReason: isDead ? form.niReason : undefined,
            reason: needsExtraReason ? SCOPE_REASON : undefined,
            sellerName: form.sellerName || undefined,
            productCategory: form.productCategory || undefined,
            leadScore: form.leadScore,
            leadScoreAuto: LEAD_SCORE_AUTO[form.outcome] ?? null,
          });
          wa.go();
          // Form is in — release the latch. Predictive returns before the
          // progressive clear below, so without this the form never closed.
          clearPendingDisposition();
          setPendingDisp(null);
          onSubmitted && onSubmitted();
        } catch (e) {
          wa.cancel();
          if (e?.response?.data?.code === 'NI_CAP_EXCEEDED') {
            alert(e.response.data.message || 'Monthly Not-Interested cap reached for this buyer — cannot mark as Not Interested.');
            setSubmitting(false);
            return; // let the agent pick a different outcome
          }
          // any other hiccup — parent's next poll still recovers the session
        }
        setSubmitting(false);
        return;
      }

      const p10 = String(phone).replace(/\D/g, '').slice(-10);
      // 1) log the outcome — CallReport row + WhatsApp conversation state +,
      // for DEAD, the CANONICAL not-interested flow (same one the WhatsApp
      // lead's own "Buyer Not Interested" menu uses — cap + reason routing included).
      try {
        await axios.post(AI_PATH + '/api/ai/voice/call-outcome', {
          phone: p10,
          outcome: form.outcome,
          followUpAt: followUpTime,
          remark: (isDead || needsExtraReason) ? '' : form.callRemark,
          notInterestedReason: isDead ? form.niReason : undefined,
          reason: needsExtraReason ? SCOPE_REASON : undefined,
          talktime: current?.talktime,
          unregisteredLeadLogsId: current?.unregisteredLeadLogsId,
          sellerName: form.sellerName || undefined,
          productCategory: form.productCategory || undefined,
          leadScore: form.leadScore,
          leadScoreAuto: LEAD_SCORE_AUTO[form.outcome] ?? null,
        });
        wa.go();
      } catch (e) {
        wa.cancel();
        if (e?.response?.data?.code === 'NI_CAP_EXCEEDED') {
          alert(e.response.data.message || 'Monthly Not-Interested cap reached for this buyer — cannot mark as Not Interested.');
          setSubmitting(false);
          return; // do NOT advance the queue — let the agent pick a different outcome
        }
        // any other outcome-log hiccup shouldn't block advancing the dialer
      }

      // 2) advance (or pause) the dialer queue — the BACKEND dials the next lead.
      await axios.post(AI_PATH + '/api/ai/voice/dialer/disposition', {
        callStatus: outcomeMeta.legacyStatus,
        // The REAL choice, sent alongside the legacy string. legacyStatus
        // collapses HOT, WARM and COLD into "Follow-up", so the per-dial row
        // in dialercalllogs could never tell them apart — and that row is the
        // only per-call history we keep. Stored as `outcomeCode`, never over
        // `outcome`, which has live readers expecting the legacy vocabulary.
        outcomeCode: outcomeMeta.value,
        followUpTime,
      });
      // Form is in — release the latch.
      clearPendingDisposition();
      setPendingDisp(null);
      onSubmitted && onSubmitted();
    } catch (e) { /* surface via parent re-poll */ }
    finally { setSubmitting(false); }
  };

  // Score is COMPULSORY for every outcome except SCOPE, and must not
  // contradict the outcome — both mirrored server-side in recordCallOutcome().
  const scoreMissing = scoreNeeded(form.outcome) && (form.leadScore === null || form.leadScore === undefined);
  const scoreClash = !!scoreContradiction(form.outcome, form.leadScore);
  const submitDisabled =
    submitting || !form.outcome ||
    (needsDate && !form.followUpDateTime) ||
    (isDead && !form.niReason) ||
    scoreMissing || scoreClash;

  return (
    <Box style={{
      width: SIDEBAR_W, minWidth: SIDEBAR_W, borderLeft: `1px solid ${C.border}`,
      background: '#fff', height: '100%', display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <Box style={{
        padding: '10px 14px', borderBottom: `1px solid ${C.border}`,
        background: C.bgSoft, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <span style={{ fontSize: 16 }}>📞</span>
          <Box>
            <Typography style={{ fontWeight: 800, color: C.primary, fontSize: 14, lineHeight: 1.1 }}>
              {isPredictive ? 'Predictive Call' : 'Progressive Dialer'}
            </Typography>
            {!isPredictive && (
              <Typography style={{ fontSize: 11, color: C.muted }}>
                {(dialer?.cursor ?? 0) + (state === 'done' ? 0 : 1)} of {dialer?.total ?? 0}
                {dialer?.stats ? ` · ${dialer.stats.connected || 0} connected` : ''}
              </Typography>
            )}
          </Box>
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: 2 }}>
          <IconButton
            size="small"
            title="Load this number's WhatsApp chat (if it didn't follow the call)"
            onClick={() => onRefreshChat && onRefreshChat(current?.phone)}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" title="Stop dialler" onClick={onStop}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* FBL Prime member on the line — plan + free MRP left this cycle
          (members only; renders nothing otherwise). Shailendra 2026-10-03. */}
      {phone && <PrimeDialerBanner phone={phone} />}

      {/* PAUSED banner */}
      {state === 'paused' && (
        <Box style={{ padding: 14 }}>
          <Box style={{
            padding: 14, borderRadius: 8, background: '#fff8e1', border: '1px solid #ffe082',
          }}>
            <Typography style={{ fontWeight: 800, color: '#8d6e00', fontSize: 14, marginBottom: 4 }}>
              ⏸ Dialer paused
            </Typography>
            <Typography style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
              {dialer?.pausedReason === 'order_placed'
                ? 'Order placed — punch the order + take payment, then resume when ready.'
                : (dialer?.pausedReason === 'softphone_offline'
                  ? 'Your VOIZ softphone went offline. Log back in, then resume.'
                  : 'Paused. Resume to dial the next number.')}
            </Typography>
            <Button
              fullWidth variant="contained"
              onClick={() => onResume && onResume()}
              style={{ background: C.good, color: '#fff', fontWeight: 700 }}
            >
              ▶ Resume — dial next
            </Button>
          </Box>
        </Box>
      )}

      {/* COOLDOWN — buyer disconnected / no answer → 3-2-1 before re-dial */}
      {inCooldown && (
        <Box style={{ padding: 16, textAlign: 'center' }}>
          <Box style={{ padding: 16, borderRadius: 10, background: '#fff4f4', border: '1px solid #ffcdd2' }}>
            <Typography style={{ fontSize: 13, fontWeight: 800, color: C.danger }}>
              {dialer?.cooldownReason === 'buyer_disconnected' ? 'Buyer disconnected the call'
                : dialer?.cooldownReason === 'dial_rejected' ? 'Dialer busy — retrying'
                : 'No answer'}
            </Typography>
            <Typography style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
              {dialer?.cooldownReason === 'dial_rejected'
                ? 'The line was busy for a moment. Retrying the same number in:'
                : 'Take a breath — the last call has ended. Dialing the next number in:'}
            </Typography>
            <Typography style={{ fontSize: 56, fontWeight: 800, color: C.primary, lineHeight: 1.1, margin: '6px 0 2px', fontVariantNumeric: 'tabular-nums' }}>
              {countdown}
            </Typography>
            <Typography style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
              Next: {current?.phone || '—'}{current?.name ? ` · ${current.name}` : ''}
              {BUCKET_BADGE[current?.bucket] && <>{' '}<LeadBadge bucket={current.bucket} size="sm" /></>}
            </Typography>
            <Box style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <Button
                variant="contained" style={{ flex: 1, background: C.good, color: '#fff', fontWeight: 700 }}
                onClick={() => { cooldownFiredRef.current = true; onDialNext && onDialNext(); }}
              >
                Dial now
              </Button>
              <Button
                variant="outlined" style={{ flex: 1, color: '#8d6e00', borderColor: '#ffe082', fontWeight: 700 }}
                onClick={() => { cooldownFiredRef.current = true; onHold && onHold(); }}
              >
                Hold
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* LIVE CALL — End Call */}
      {!inCooldown && state !== 'paused' && callState === 'dialing' && (
        <Box style={{ padding: 16, textAlign: 'center' }}>
          <Typography style={{ fontSize: 12, color: C.muted, fontWeight: 700, letterSpacing: 0.4 }}>
            {isPredictive ? 'PREDICTIVE CALL' : 'ON CALL'}
          </Typography>
          <Typography style={{ fontSize: 20, fontWeight: 800, color: C.text, margin: '2px 0 2px' }}>
            {phone}
          </Typography>
          {/* FRESH / MISSED / FOLLOW-UP right under the number the agent is talking to. */}
          {BUCKET_BADGE[current?.bucket] && (
            <Box style={{ marginBottom: 6 }}>
              <LeadBadge bucket={current.bucket} />
              {(current.bucket === 'fresh' || current.bucket === 'fresh_redistributed') && (
                <Typography style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>
                  New lead — first contact. Give it your best pitch.
                </Typography>
              )}
              {current.bucket === 'followup' && (current.followUpReason || current.followUpScheduledAt) && (
                <Typography style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
                  Scheduled because "{current.followUpReason || 'a follow-up was promised'}"
                  {current.followUpScheduledAt ? ` · promised for ${fmtDateTime(current.followUpScheduledAt)}` : ''}
                </Typography>
              )}
            </Box>
          )}
          <Typography style={{ fontSize: 28, fontWeight: 800, color: C.primary, fontVariantNumeric: 'tabular-nums', marginBottom: 16 }}>
            {fmtDur(elapsed)}
          </Typography>
          {showBuyerInfo && (
            <Box style={{ textAlign: 'left', padding: '10px 12px', marginBottom: 14, borderRadius: 8, background: C.bgSoft, border: `1px solid ${C.border}` }}>
              <Box display="flex" alignItems="flex-start" justifyContent="space-between" style={{ gap: 8, marginBottom: 6 }}>
                <Box style={{ minWidth: 0 }}>
                  <Typography style={{ fontSize: 13, fontWeight: 800, color: C.text }}>
                    {buyerInfo?.name || buyerInfo?.whatsapp?.name || 'Unknown buyer'}
                  </Typography>
                  {buyerInfo?.whatsapp?.name && buyerInfo.whatsapp.name !== buyerInfo?.name && (
                    <Typography style={{ fontSize: 11, color: C.muted }}>WhatsApp: {buyerInfo.whatsapp.name}</Typography>
                  )}
                </Box>
                {/* Disabled (not clickable) when this number has no WA chat. */}
                <Button
                  size="small" variant="outlined"
                  disabled={!waConvoId}
                  onClick={openChat}
                  title={waConvoId ? "Open this buyer's WhatsApp chat" : 'No WhatsApp chat found for this number'}
                  style={{ flexShrink: 0, textTransform: 'none', fontWeight: 700, fontSize: 11, padding: '2px 8px' }}
                >
                  {waConvoId ? 'Open Chat' : 'No Chat'}
                </Button>
              </Box>
              <Typography style={{ fontSize: 11, color: C.muted, lineHeight: 1.8 }}>
                {buyerInfo?.whatsapp && (
                  <>
                    Ad campaign: {buyerInfo.whatsapp.adCampaignName || buyerInfo.whatsapp.adName || buyerInfo.whatsapp.adHeadline || (buyerInfo.whatsapp.source === 'organic' ? 'Organic (no ad)' : '—')}
                    {buyerInfo.whatsapp.product && <><br />Asked about: {buyerInfo.whatsapp.product}</>}
                    <br />
                  </>
                )}
                Last order: {buyerInfo?.lastOrder
                  ? <>#{buyerInfo.lastOrder.orderId || '—'} · {fmtDate(buyerInfo.lastOrder.date)} · ₹{buyerInfo.lastOrder.amount ?? '—'}</>
                  : 'No prior order'}
                <br />
                Last call: {buyerInfo?.lastCallAt ? fmtDate(buyerInfo.lastCallAt) : 'No prior call on record'}
              </Typography>
              {buyerInfo?.whatsapp?.firstMessage?.text && (
                <Box style={{ marginTop: 6, padding: '6px 8px', borderRadius: 6, background: '#fff', border: `1px solid ${C.border}` }}>
                  <Typography style={{ fontSize: 10, fontWeight: 800, color: C.muted, letterSpacing: 0.4 }}>
                    FIRST MESSAGE · {fmtDate(buyerInfo.whatsapp.firstMessage.at)}
                  </Typography>
                  <Typography style={{ fontSize: 12, color: C.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 90, overflowY: 'auto' }}>
                    {buyerInfo.whatsapp.firstMessage.text}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          {/* Talked to the buyer → wrap-up form. Wrong/invalid/no-pickup →
              cooldown, no form. Two explicit choices so the agent isn't forced
              to fill a form for a number that never connected. */}
          <Button
            fullWidth variant="contained" size="large"
            disabled={ending}
            onClick={() => endCall(true)}
            startIcon={ending ? <CircularProgress size={16} style={{ color: '#fff' }} /> : <CallEndIcon />}
            style={{ background: C.danger, color: '#fff', fontWeight: 800, padding: '12px 0', fontSize: 16 }}
          >
            {ending ? 'Ending…' : 'End & fill form'}
          </Button>
          <Button
            fullWidth variant="outlined" size="small"
            disabled={ending}
            onClick={() => { clearPendingDisposition(); setPendingDisp(null); endCall(false); }}
            style={{ marginTop: 8, color: C.muted, borderColor: C.border, fontWeight: 700, textTransform: 'none' }}
          >
            Wrong / invalid number · no answer → skip
          </Button>
          {endWarning && (
            <Box style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: '#fdecea', border: '1px solid #f5c2c0', textAlign: 'left' }}>
              <Typography style={{ fontSize: 12, fontWeight: 700, color: '#b71c1c' }}>{endWarning}</Typography>
            </Box>
          )}
          <Typography style={{ fontSize: 11, color: C.muted, marginTop: 10 }}>
            Talked to the buyer? "End & fill form". Wrong number or no pickup?
            "skip" — no form, moves to the next after a short countdown.
          </Typography>

          {/* This number's history WITH THIS AGENT — did an earlier attempt
              already connect, or has it been ringing out every time. */}
          <Box style={{ marginTop: 16, textAlign: 'left', borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
            <Typography style={{ fontSize: 11, fontWeight: 800, color: C.muted, letterSpacing: 0.4, marginBottom: 6 }}>
              YOUR HISTORY WITH THIS NUMBER
            </Typography>
            {numberHistoryLoading && (
              <Box display="flex" justifyContent="center" style={{ padding: 8 }}>
                <CircularProgress size={16} />
              </Box>
            )}
            {!numberHistoryLoading && numberHistory.length === 0 && (
              <Typography style={{ fontSize: 12, color: C.muted }}>
                First time you're calling this number.
              </Typography>
            )}
            {!numberHistoryLoading && numberHistory.length > 0 && (
              <Box style={{ maxHeight: 220, overflowY: 'auto' }}>
                {numberHistory.map((c) => {
                  const { Icon, color, label } = numberHistoryVisual(c);
                  const isPlaying = playingCallId === c._id;
                  return (
                    <Box key={c._id} style={{ padding: '4px 0' }}>
                      <Box display="flex" alignItems="center">
                        <Icon style={{ color, fontSize: 15, marginRight: 8, flexShrink: 0 }} />
                        <Typography style={{ fontSize: 12, color: C.text, flex: 1 }}>
                          {fmtDateTime(c.startedAt || c.connectedAt)}
                        </Typography>
                        <Typography style={{ fontSize: 12, color, fontWeight: 700, whiteSpace: 'nowrap', marginRight: c.recordingUrl ? 4 : 0 }}>
                          {label}{c.talktime > 0 ? ` · ${fmtDur(c.talktime)}` : ''}
                        </Typography>
                        {/* Recording playback (2026-09-15) — only present once the
                            call has connected, per Phonomenal's call_recording_url. */}
                        {c.recordingUrl && (
                          <IconButton
                            size="small"
                            onClick={() => setPlayingCallId(isPlaying ? null : c._id)}
                            style={{ padding: 2, marginLeft: 2 }}
                            title={isPlaying ? 'Hide recording' : 'Play recording'}
                          >
                            {isPlaying
                              ? <PauseCircleOutlineIcon style={{ fontSize: 18, color: C.primary }} />
                              : <PlayCircleOutlineIcon style={{ fontSize: 18, color: C.primary }} />}
                          </IconButton>
                        )}
                      </Box>
                      {isPlaying && c.recordingUrl && (
                        <audio
                          controls autoPlay src={c.recordingUrl}
                          style={{ width: '100%', height: 28, marginTop: 4 }}
                          onEnded={() => setPlayingCallId(null)}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* WA CALL SUMMARY — mandatory pre-disposition step, only when the Meta
          24h window is open for this conversation (Shailendra 2026-09-07) */}
      {!inCooldown && state !== 'paused' && callState === 'ended' && needsWaSummary && (
        <Box style={{ padding: 14, overflowY: 'auto', flex: 1 }}>
          <Typography style={{ fontSize: 13, fontWeight: 800, color: C.text }}>
            {phone}
          </Typography>
          <Typography style={{ fontSize: 12, color: C.muted, fontWeight: 600, marginBottom: 10 }}>
            Call ended · {fmtDur(current?.talktime)} talk time · WhatsApp the buyer before you fill the call form
          </Typography>

          <Box style={{
            padding: '10px 12px', borderRadius: 8, marginBottom: 10,
            background: '#e8f5e9', border: '1px solid #25D366',
          }}>
            <Typography style={{ fontSize: 12.5, fontWeight: 800, color: '#137333', marginBottom: 4 }}>
              📲 WhatsApp the buyer from the chat box
            </Typography>
            <Typography style={{ fontSize: 12, color: C.text, lineHeight: 1.45 }}>
              Type in the chat's <b>"Type a message"</b> box and press <b>Send</b> (min 30 characters).
              This message goes to the <b>buyer</b> — e.g. "Hi Sir, we just got over the call — preparing your bill and sending the PI shortly."
            </Typography>
            <Typography style={{ fontSize: 11.5, color: C.danger, fontWeight: 700, marginTop: 6 }}>
              Don't write call remarks there ("cx busy", "call back later") — you'll fill those in the call form right after.
            </Typography>
          </Box>

          {!waChatOpen && current?.conversationId && onOpenChat && (
            <Button
              fullWidth variant="outlined" size="small"
              onClick={() => onOpenChat(current.conversationId)}
              style={{ marginBottom: 8 }}
            >
              Open this buyer's chat
            </Button>
          )}

          {waSummaryError && (
            <>
              <Box style={{
                padding: '8px 10px', borderRadius: 8, marginBottom: 8,
                background: '#fdecea', border: `1px solid ${C.danger}`,
              }}>
                <Typography style={{ fontSize: 11.5, color: C.danger, fontWeight: 700 }}>
                  {waSummaryError}
                </Typography>
              </Box>
              <Button
                fullWidth variant="text" size="small"
                onClick={() => setWaSummaryBypassed(true)}
                style={{ color: C.muted, fontSize: 11.5 }}
              >
                Continue without sending
              </Button>
            </>
          )}
        </Box>
      )}

      {/* WRAP-UP — disposition form */}
      {/* COHORT: the repeat team's own form, posting to /voice/cohort-outcome.
          Rendered INSTEAD of the new-revenue outcome list below, so a cohort
          agent sees the same nine outcomes whether the call came from the
          predictive engine, the progressive queue, or a manual click-to-call.
          onClose advances the dialler exactly as the new-rev submit does. */}
      {cohort && !inCooldown && state !== 'paused' && callState === 'ended' && !needsWaSummary && (
        <CohortCallDisposition
          dispo={{
            open: true,
            phone,
            name: current?.name,
            talktime: current?.talktime,
            sessionId: current?.sessionId,
          }}
          afterSubmit={async () => {
            // PREDICTIVE: the outcome is saved, but Phonomenal still holds the
            // session — and only clearing it lets the engine dial again. The
            // endpoint needs an `outcome` for its own bookkeeping; the cohort
            // taxonomy is already recorded, so send the neutral one and let
            // /voice/cohort-outcome remain the single source of truth.
            if (isPredictive) {
              await axios.post(AI_PATH + '/api/ai/voice/predictive/session/dispose', { outcome: 'COLD' });
              return;
            }
            // PROGRESSIVE: advance (or pause) the queue, exactly as the
            // new-revenue submit does. callStatus 'Connected' is the honest
            // legacy value for a reached call — the real outcome is on the lead.
            await axios.post(AI_PATH + '/api/ai/voice/dialer/disposition', { callStatus: 'Connected' });
          }}
          onClose={() => { onSubmitted && onSubmitted(); }}
          redial={{ onClick: redial, busy: redialing, error: redialError }}
        />
      )}

      {/* `pendingDisp` keeps the form open across a browser refresh — without it
          React state dies with the page and the call goes undispositioned. */}
      {!cohort && !inCooldown && state !== 'paused' && (callState === 'ended' || pendingDisp) && !needsWaSummary && (
        <Box style={{ padding: 14, overflowY: 'auto', flex: 1 }}>
          <Typography style={{ fontSize: 13, fontWeight: 800, color: C.text }}>
            {phone}
          </Typography>
          <Typography style={{ fontSize: 12, color: C.muted, fontWeight: 600, marginBottom: 10 }}>
            Call ended · {fmtDur(current?.talktime)} talk time · how did it go?
          </Typography>

          {/* FIRST THING ON THE FORM (Shailendra 2026-10-21). It used to sit
              under the outcome cards and agents submitted straight past it —
              2,198 scores on day one, only 8% with the slider actually moved. */}
          <LeadScoreBar
            outcome={form.outcome}
            value={form.leadScore}
            onChange={(v) => setField('leadScore', v)}
          />

          <TextField
            fullWidth size="small" margin="dense" variant="outlined"
            label={nameLocked ? 'Name' : 'Name (optional)'}
            value={form.sellerName}
            onChange={(e) => setField('sellerName', e.target.value)}
            disabled={nameLocked}
          />
          <FormControl fullWidth size="small" margin="dense" variant="outlined" disabled={categoryLocked}>
            <InputLabel>{categoryLocked ? 'Product Category' : 'Product Category (optional)'}</InputLabel>
            <Select
              label={categoryLocked ? 'Product Category' : 'Product Category (optional)'}
              value={form.productCategory}
              onChange={(e) => setField('productCategory', e.target.value)}
            >
              {form.productCategory && !categoryOptions.includes(form.productCategory) && (
                <MenuItem value={form.productCategory}>{form.productCategory}</MenuItem>
              )}
              {categoryOptions.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>

          {OUTCOMES.map((o) => {
            const selected = form.outcome === o.value;
            const Icon = o.Icon;
            return (
              <React.Fragment key={o.value}>
              <Box
                onClick={() => {
                  // Seed the score bar from the outcome; the agent may then
                  // drag it anywhere. Both numbers are sent so the server can
                  // measure how far agents move off our seed.
                  setForm((f) => ({
                    ...f,
                    outcome: o.value,
                    leadScore: null, /* no seed: the agent must set the score themselves (Shailendra 2026-10-21) */
                  }));
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px',
                  marginBottom: 7, borderRadius: 9, cursor: 'pointer',
                  border: `1.5px solid ${selected ? o.color : C.border}`,
                  background: selected ? o.bg : '#fff',
                  transition: 'border-color .15s, background .15s',
                }}
              >
                <Box style={{
                  width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: selected ? o.color : '#f1f3f5',
                }}>
                  <Icon style={{ fontSize: 17, color: selected ? '#fff' : C.muted }} />
                </Box>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Typography style={{ fontSize: 12.5, fontWeight: 800, color: selected ? o.color : C.text, lineHeight: 1.25 }}>
                    {o.label}{o.tag && <span style={{ color: o.color, fontWeight: 400 }}> {o.tag}</span>}
                  </Typography>
                  <Typography style={{ fontSize: 10.5, color: C.muted, lineHeight: 1.3 }}>
                    {o.sub}
                  </Typography>
                </Box>
                <Box style={{
                  width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                  border: `2px solid ${selected ? o.color : '#c9ced6'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {selected && <Box style={{ width: 8, height: 8, borderRadius: '50%', background: o.color }} />}
                </Box>
              </Box>
              {/* Follow-up date / reason opens right under the picked card,
                  not at the bottom of the form. */}
              {selected && (
                <Box style={{ marginTop: -4, marginBottom: 8 }}>
                  <Collapse in={needsDate}>
                    <TextField
                      fullWidth size="small" label="Follow-up date & time" type="datetime-local"
                      margin="dense" variant="outlined" InputLabelProps={{ shrink: true }}
                      value={form.followUpDateTime}
                      onChange={(e) => setField('followUpDateTime', e.target.value)}
                      inputProps={{ min: new Date().toISOString().slice(0, 16) }}
                      style={{ marginTop: 4 }}
                      InputProps={{ startAdornment: <EventIcon style={{ fontSize: 16, color: C.muted, marginRight: 6 }} /> }}
                    />
                  </Collapse>

                  <Collapse in={isDead}>
                    <FormControl fullWidth size="small" margin="dense" variant="outlined" style={{ marginTop: 4 }}>
                      <InputLabel>Reason for Not Interested</InputLabel>
                      <Select
                        MenuProps={DROPDOWN_BELOW}
                        label="Reason for Not Interested"
                        value={form.niReason}
                        onChange={(e) => setField('niReason', e.target.value)}
                      >
                        {NI_REASONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Collapse>
                </Box>
              )}
              </React.Fragment>
            );
          })}

          {!isDead && !needsExtraReason && (
            <TextField
              fullWidth size="small" label="Remarks (optional)" margin="dense" variant="outlined"
              multiline minRows={2}
              value={form.callRemark}
              onChange={(e) => setField('callRemark', e.target.value)}
            />
          )}

          <Button
            fullWidth variant="contained" size="large"
            disabled={submitDisabled}
            onClick={submit}
            startIcon={submitting ? <CircularProgress size={16} style={{ color: '#fff' }} /> : <SendIcon style={{ fontSize: 17 }} />}
            style={{
              marginTop: 12, fontWeight: 800, padding: '11px 0', fontSize: 14,
              background: submitDisabled ? '#cbd5e1' : (outcomeMeta ? outcomeMeta.color : C.good),
              color: '#fff', textTransform: 'none',
            }}
          >
            {submitting ? 'Saving…' : (isOrder ? 'Submit & Pause (punch order)' : 'Submit & Next Call')}
          </Button>

          {/* Redial — the call got cut by mistake / connected wrongly and the
              agent wants to try the SAME number again (doesn't advance). */}
          {redialButton}
        </Box>
      )}

      {/* between calls (no current item / done) */}
      {!inCooldown && state !== 'paused' && !callState && (
        <Box style={{ padding: 20, textAlign: 'center', color: C.muted }}>
          <CircularProgress size={22} />
          <Typography style={{ fontSize: 12, marginTop: 10 }}>
            {state === 'done' ? 'Dialer finished — all numbers done.' : 'Connecting the next call…'}
          </Typography>
        </Box>
      )}

      {/* CATCH-ALL recovery — a state/callState combination none of the panes
          above render (e.g. awaiting_disposition parked on a non-'ended' item
          after an end-call race). Without this the pane goes BLANK and the agent
          can't proceed OR stop → frozen dialer. The backend status() self-heals
          this on the next poll, but show a manual recovery here so the agent is
          never stuck: Dial next (skip the wedged wrap-up) or Stop. */}
      {!inCooldown && state !== 'paused'
        && callState && callState !== 'dialing' && callState !== 'ended' && (
        <Box style={{ padding: 16, textAlign: 'center' }}>
          <Box style={{ padding: 16, borderRadius: 10, background: '#fff8e1', border: '1px solid #ffe082' }}>
            <Typography style={{ fontSize: 13, fontWeight: 800, color: '#8d6e00' }}>
              Nothing to wrap up here
            </Typography>
            <Typography style={{ fontSize: 12, color: C.muted, marginTop: 4, marginBottom: 12 }}>
              The last call was already logged. Move to the next number, or stop the dialer.
            </Typography>
            <Box style={{ display: 'flex', gap: 8 }}>
              <Button
                variant="contained" style={{ flex: 1, background: C.good, color: '#fff', fontWeight: 700 }}
                onClick={() => onDialNext && onDialNext()}
              >
                Dial next
              </Button>
              <Button
                variant="outlined" style={{ flex: 1, color: C.danger, borderColor: C.border, fontWeight: 700 }}
                onClick={() => onStop && onStop()}
              >
                Stop
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
